/**
 * RMG Game Client - React App Entry Point
 *
 * Provides authentication wrapper around the game showcase.
 * Uses Amplify Authenticator for sign-in/sign-up.
 */

import { Amplify } from 'aws-amplify';
import { useEffect, type ReactNode } from 'react';

import { AuthenticatorWrapper, useAuthenticator } from './components/auth/AuthenticatorWrapper';
import { PasskeyPrompt } from './components/auth/PasskeyPrompt';
import { getAmplifyConfig, isCognitoConfigured } from './config/amplify';

import './style.css';

// Configure Amplify on app load
const amplifyConfig = getAmplifyConfig();
if (isCognitoConfigured()) {
  Amplify.configure(amplifyConfig);
}

/**
 * Set auth cookie for CloudFront Function to check.
 * This enables edge-level auth protection.
 */
function setAuthCookie(authenticated: boolean): void {
  if (authenticated) {
    // Set cookie with secure flags - expires in 24 hours
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `rmg_auth=true; path=/; expires=${expires}; SameSite=Strict; Secure`;
  } else {
    // Clear cookie on logout
    document.cookie =
      'rmg_auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict; Secure';
  }
}

/**
 * Main authenticated app content.
 * Loads the vanilla JS game dashboard after authentication.
 */
function AuthenticatedContent(): ReactNode {
  const { user, signOut } = useAuthenticator();

  useEffect(() => {
    // Set auth cookie for CloudFront edge protection
    setAuthCookie(true);

    // Import and initialize the game dashboard
    const initDashboard = async (): Promise<void> => {
      const mainContent = document.getElementById('main-content');
      if (!mainContent) return;

      // Dynamic import to load the game logic
      const { renderDashboard } = await import('./dashboard');
      renderDashboard(mainContent);
    };

    void initDashboard();
  }, []);

  // Wrap signOut to clear auth cookie
  const handleSignOut = (): void => {
    setAuthCookie(false);
    signOut();
  };

  return (
    <div id="app">
      <header className="top-nav glass-panel">
        <div className="logo">
          <span className="logo-r">R</span>MG
        </div>
        <div className="header-right">
          <div className="user-info">
            <span className="user-email">{user.signInDetails?.loginId ?? 'Player'}</span>
            <button className="sign-out-btn" onClick={handleSignOut} type="button">
              Sign Out
            </button>
          </div>
          <div className="wallet-info">
            <span className="balance-label">Balance</span>
            <span className="balance-value" id="wallet-balance">
              £10,000.00
            </span>
          </div>
        </div>
      </header>

      <main id="main-content">{/* Game dashboard will be injected here */}</main>

      <PasskeyPrompt />
    </div>
  );
}

/**
 * App without authentication.
 * Used when Cognito is not configured (local dev without auth).
 */
function UnauthenticatedApp(): ReactNode {
  useEffect(() => {
    const initDashboard = async (): Promise<void> => {
      const mainContent = document.getElementById('main-content');
      if (!mainContent) return;

      const { renderDashboard } = await import('./dashboard');
      renderDashboard(mainContent);
    };

    void initDashboard();
  }, []);

  return (
    <div id="app">
      <header className="top-nav glass-panel">
        <div className="logo">
          <span className="logo-r">R</span>MG
        </div>
        <div className="header-right">
          <div className="auth-notice">
            <span>Auth not configured</span>
          </div>
          <div className="wallet-info">
            <span className="balance-label">Balance</span>
            <span className="balance-value" id="wallet-balance">
              £10,000.00
            </span>
          </div>
        </div>
      </header>

      <main id="main-content">{/* Game dashboard will be injected here */}</main>
    </div>
  );
}

/**
 * Root App component.
 * Conditionally wraps with auth based on Cognito configuration.
 */
export function App(): ReactNode {
  if (!isCognitoConfigured()) {
    return <UnauthenticatedApp />;
  }

  return (
    <AuthenticatorWrapper>
      <AuthenticatedContent />
    </AuthenticatorWrapper>
  );
}
